import { FC, useEffect, useRef } from "react";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Box, { BoxProps } from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Heading from "sections/nfcTagsPage/Heading";

import styles from "./styles";

gsap.registerPlugin(ScrollTrigger);

interface ProcessSectionProps {
  boxProps?: BoxProps;
  isReadyToStart: boolean;
  setSectionLoaded: () => void;
}

const STEPS: { label: string }[] = [
  { label: "Provide item details" },
  { label: "We digitize physical product" },
  { label: "We deliver ready-to-attach custom labels" },
  { label: "Sell your items as usual" },
];

const STEPS_COUNT = STEPS.length;
const BASE_DURATION = 1000;

const ProcessSection: FC<ProcessSectionProps> = ({
  boxProps,
  isReadyToStart,
  setSectionLoaded,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const sectionRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isReadyToStart) return;

    const leftOffset = sectionRef.current?.getBoundingClientRect().left || 0;
    const barWidth = barRef.current?.getBoundingClientRect().width || 0;

    const tl = gsap.timeline({
      duration: 100,
      scrollTrigger: {
        trigger: "#ps-wrapper",
        start: isDesktop ? "center center" : "top 70%",
        end: isDesktop ? "+=1000" : "bottom center",
        scrub: 1,
        anticipatePin: 1,
        pin: isDesktop ? "#ps-wrapper" : undefined,
        toggleActions: "restart none none reverse",
      },
    });

    if (isDesktop) {
      tl.fromTo(
        "#ps-bar",
        { maxWidth: `${barWidth}px` },
        {
          maxWidth: `${barWidth - leftOffset}px`,
          duration: (leftOffset / (barWidth - leftOffset)) * BASE_DURATION,
          ease: "linear",
        },
      );
      tl.fromTo(
        "#ps-bar",
        { maxWidth: `${barWidth - leftOffset}px` },
        { maxWidth: "0px", duration: BASE_DURATION, ease: "linear" },
      );

      // Show texts
      tl.from(
        ".ps-typo",
        {
          yPercent: -100,
          opacity: 0,
          duration: 125,
          stagger: BASE_DURATION / STEPS_COUNT,
        },
        "<",
      );
      tl.from(
        ".ps-label",
        {
          yPercent: 100,
          opacity: 0,
          duration: 200,
          stagger: BASE_DURATION / STEPS_COUNT,
        },
        "<",
      );
    } else {
      tl.fromTo(
        "#ps-bar",
        { maxHeight: "100%" },
        {
          maxHeight: "0%",
          duration: BASE_DURATION,
          ease: "linear",
        },
      );

      // Show texts
      tl.from(
        ".ps-typo",
        {
          yPercent: 100,
          opacity: 0,
          duration: 125,
          stagger: BASE_DURATION / STEPS_COUNT,
        },
        "<",
      );
      tl.from(
        ".ps-label",
        {
          yPercent: 100,
          opacity: 0,
          duration: 200,
          stagger: BASE_DURATION / STEPS_COUNT,
        },
        "<",
      );
    }
    setSectionLoaded();
  }, [isDesktop, isReadyToStart, setSectionLoaded]);

  return (
    <Box component="section" {...boxProps} ref={sectionRef}>
      <Heading heading="Process" />

      <Box sx={styles.wrapper} id="ps-wrapper">
        <Box sx={styles.barWrapper} ref={barRef}>
          <Box sx={styles.bar} />
          <Box sx={styles.barSlider} id="ps-bar" />
        </Box>

        <Box sx={styles.index}>
          {STEPS.map((_, index) => (
            <Typography
              className="ps-typo"
              key={index}
              component="h3"
              sx={styles.indexText}
            >
              {index + 1}
            </Typography>
          ))}
        </Box>

        <Box sx={styles.label}>
          {STEPS.map((step, index) => (
            <Typography
              className="ps-label"
              key={index}
              variant="h2"
              component="h3"
              sx={styles.labelText}
            >
              {step.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ProcessSection;
